import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight, CheckCircle, AlertCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CALENDLY_URL = 'https://calendly.com/ismail-haamud98/30min';

const HeroSection = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';
  const [status, setStatus] = useState({ registration_open: true, student_limit: 30, current_students: 0 });

  useEffect(() => {
    fetch(`${API_URL}/api/public/registration-status`).then(r => r.json()).then(d => { if (d?.registration_open !== undefined) setStatus(d); }).catch(() => {});
  }, []);

  const trustFeatures = sv
    ? ['Gratis provlektion', 'Certifierad tajweed-undervisning', 'Små grupplektioner eller privatlektion', 'För barn och vuxna', 'Flexibla tider online']
    : ['Free trial lesson', 'Certified tajweed teaching', 'Small group or private lessons', 'For children and adults', 'Flexible times online'];

  const spotsLeft = status ? status.student_limit - status.current_students : null;
  const isOpen = status?.registration_open;
  const isFull = spotsLeft !== null && spotsLeft <= 0;
  const isLow = spotsLeft !== null && spotsLeft > 0 && spotsLeft <= 5;

  return (
    <section className="hero-gradient islamic-pattern min-h-[90vh] flex items-center relative pt-20" data-testid="hero-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">

        {/* Spots banner */}
        {status && (
          <div className="mb-6" data-testid="spots-banner">
            {isOpen && !isFull && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isLow ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-gold/10 text-gold border border-gold/20'}`}>
                {isLow ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
                {sv
                  ? `Begränsade platser — ${spotsLeft} ${spotsLeft === 1 ? 'plats' : 'platser'} kvar`
                  : `Limited spots — ${spotsLeft} ${spotsLeft === 1 ? 'spot' : 'spots'} left`}
              </div>
            )}
            {(!isOpen || isFull) && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">
                <Clock size={15} />
                {sv
                  ? 'Alla platser fyllda — Anmäl dig till väntelistan!'
                  : 'All spots filled — Join the waiting list!'}
              </div>
            )}
          </div>
        )}

        <p className="text-3xl md:text-4xl mb-5" style={{ color: 'var(--gold)', fontFamily: "'Amiri', serif" }} data-testid="basmallah">
          بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
        </p>
        <div className="inline-flex items-center px-4 py-1.5 rounded text-xs tracking-widest uppercase mb-8 text-gold"
          style={{ border: '1px solid rgba(206, 150, 8, 0.25)', letterSpacing: '2px' }}>
          Quran Online Sverige
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: 'var(--text-primary)' }}>
          {sv ? 'Lär dig läsa och recitera Koranen korrekt' : 'Learn to read and recite the Quran correctly'}
        </h1>
        <p className="text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {sv
            ? 'Personlig undervisning i Koranläsning och tajweed för barn och vuxna – online.'
            : 'Personal Quran reading and tajweed instruction for children and adults – online.'}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center justify-center gap-2" data-testid="hero-cta-booking">
            {isOpen && !isFull
              ? (sv ? 'Reservera din plats' : 'Reserve your spot')
              : (sv ? 'Ställ dig i kö' : 'Join waiting list')} <ArrowRight size={18} />
          </a>
          <Link to="/programs" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="hero-cta-programs">
            {sv ? 'Se våra kurser' : 'See our courses'}
          </Link>
        </div>

        {/* Waiting list note */}
        {status && (!isOpen || isFull) && (
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }} data-testid="waitlist-note">
            {sv
              ? 'Du anmäler dig och väntar på din tur. Vi kontaktar dig när en plats blir ledig.'
              : 'Sign up and wait for your turn. We will contact you when a spot opens up.'}
          </p>
        )}

        {/* Urgency note when spots are low */}
        {isOpen && isLow && (
          <p className="text-sm mb-8 text-red-400" data-testid="urgency-note">
            {sv
              ? 'Skynda dig! Registreringen stängs snart.'
              : 'Hurry! Registration closes soon.'}
          </p>
        )}

        {/* Trust features */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mt-4" data-testid="trust-features">
          {trustFeatures.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <CheckCircle size={15} className="text-gold flex-shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
